// Settings > Picking > Experiments > Warehouse layout.
//
// A plan view of the racking, in metres, that the walk-order work is built on.
//
// Why a drawing and not a form. Three questions decide whether a proposed pick
// route is actually shorter, and none of them can be answered from the
// locations table: which bay faces which across a corridor, where the picker
// can cut between corridors, and how far anything actually is. The first is
// stated nowhere (corridor-walk.js infers it from interleaved walk orders and
// gives up on Pembroke's G/H entirely), the second is unmodelled — the F and G
// tunnels are real locations that every cost model has silently priced as a
// long move — and the third has never had a unit, because no walking speed
// exists anywhere in the codebase.
//
// Nobody draws 1,400 bays by hand, so the model is SEEDED from `locations`:
// aisles, bays and levels are rows, corridor pairing comes from `zone`, travel
// direction from the order walk_order visits an aisle's bays, and racking type
// from whether the zone says PALLET. What the data cannot know is metres, so
// bay widths, uprights, offsets and shelf heights start at the values measured
// in the 2026-08-14 walk sims and are corrected here — where a wrong one is
// obvious at a glance instead of buried in a number.
//
// Rendering is hand-written SVG with a viewBox in metres and pointer-event
// dragging. That is deliberate: the app has no 2D/diagram or drag-and-drop
// library, `main.js` is already ~810 KB gzipped, and it is served as a PWA to
// Zebra handhelds. A plan view of rectangles does not justify adding to that.
// The 3D view, which does need a library, loads it on demand instead.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
    AlertTriangle, Box, ChevronRight, Crosshair, Footprints, GitFork, Layers, Move, Plus,
    RefreshCw,
    Ruler, Save, Trash2, Undo2,
} from "lucide-react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import { useWarehouseScope } from "../../../access/useWarehouseScope";

import {
    aisleLength, bayWidthOf, baysInPhysicalOrder, computeBays, endPath, runsForTier,
    shelfIndexAt, tunnelPath,
    LayoutAisle, LayoutCorridor, LayoutCrossing, LayoutModel, LayoutPoint, LayoutRun, PlacedBay,
} from "./layoutGeometry";
// Statically imported, but three itself is not: WarehouseScene3D pulls it in
// with await import() inside its effect, so three still lands in its own chunk
// and main.js does not grow.
import WarehouseScene3D, { LivePerson } from "./WarehouseScene3D";

export type {
    LayoutAisle, LayoutCorridor, LayoutCrossing, LayoutModel, LayoutPoint, LayoutRun, PlacedBay,
};

/** One step of the live walk order. Levels matter to the 3D view only. */
interface WalkStep {
    aisle: string;
    bay: string;
    level: string | null;
    walk_order: number;
}

interface Problem {
    kind: string;
    aisle?: string; bay?: string; corridor_id?: string;
    count?: number; sample?: string[];
    detail: string;
}

interface CalibrationBand {
    band: string; n: number;
    median_metres: number | null; median_seconds: number | null;
    predicted_seconds?: number; residual_seconds?: number;
}

interface Calibration {
    moves_measured: number;
    moves_unmapped: number;
    coverage_pct: number;
    bands: CalibrationBand[];
    fit: {
        handling_s: number; move_penalty_s: number; seconds_per_metre: number;
        walk_speed_mps: number | null; r_squared: number | null;
        bands_used: number; moves_used: number;
    } | null;
    reason: string | null;
    measured_reference: Record<string, number>;
}

const POINT_KINDS = ["cage_pickup", "pack_desk", "conveyor", "drop_zone"];
const CAGE_MODES = ["with_picker", "staged_at_end", "none"];

const fmt = (n: number | null | undefined, dp = 2) =>
    n === null || n === undefined || Number.isNaN(n) ? "—" : n.toFixed(dp);

// ─── The canvas ─────────────────────────────────────────────────────────────

interface View { x: number; y: number; w: number; h: number }

/**
 * Client pixels -> viewBox units, using the SVG's own transform.
 *
 * Not the same as interpolating across getBoundingClientRect(). The canvas is
 * `preserveAspectRatio="xMidYMid meet"`, so the viewBox is scaled UNIFORMLY
 * and centred inside the element, leaving letterbox bars whenever the panel's
 * aspect ratio differs from the shed's. Interpolating across the element's box
 * assumes a non-uniform stretch, so every mapped point drifts by the size of
 * those bars — which put the zoom focus off the cursor and offset every drag.
 * getScreenCTM() is the actual transform the browser used, so inverting it is
 * exact whatever the aspect ratio.
 *
 * The arithmetic fallback is for environments with no layout engine (jsdom in
 * tests), where getScreenCTM returns null.
 */
const toViewBox = (
    svg: SVGSVGElement | null, clientX: number, clientY: number, v: View,
): { x: number; y: number } => {
    if (!svg) return { x: 0, y: 0 };
    const ctm = typeof svg.getScreenCTM === "function" ? svg.getScreenCTM() : null;
    if (ctm && typeof DOMPoint === "function") {
        const p = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
        return { x: p.x, y: p.y };
    }
    const r = svg.getBoundingClientRect();
    if (!r.width || !r.height) return { x: v.x, y: v.y };
    return {
        x: v.x + ((clientX - r.left) / r.width) * v.w,
        y: v.y + ((clientY - r.top) / r.height) * v.h,
    };
};

/**
 * A smoothed path through the points a route passes.
 *
 * A cross-aisle or tunnel joins corridors whose racking does not end flush, so
 * the raw path steps between levels. Drawn as a polyline that reads as a sharp
 * zig-zag, which nobody walks — a picker rounds the corner. Catmull-Rom
 * converted to cubic Béziers rounds it the same way, staying ON every point
 * (so the route still meets each corridor exactly where it does) while curving
 * between them.
 *
 * `tension` 0 is a straight polyline; ~0.5 is a natural walking line.
 */
const smoothPath = (
    pts: { x_m: number; along_m: number }[], tension = 0.5,
): string => {
    if (pts.length === 0) return "";
    if (pts.length === 1) return `M ${pts[0].x_m},${pts[0].along_m}`;
    if (pts.length === 2) {
        return `M ${pts[0].x_m},${pts[0].along_m} L ${pts[1].x_m},${pts[1].along_m}`;
    }
    const at = (i: number) => pts[Math.max(0, Math.min(pts.length - 1, i))];
    let d = `M ${pts[0].x_m},${pts[0].along_m}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
        const k = tension / 3;
        const c1x = p1.x_m + (p2.x_m - p0.x_m) * k;
        const c1y = p1.along_m + (p2.along_m - p0.along_m) * k;
        const c2x = p2.x_m - (p3.x_m - p1.x_m) * k;
        const c2y = p2.along_m - (p3.along_m - p1.along_m) * k;
        d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x_m},${p2.along_m}`;
    }
    return d;
};

/** View units per screen pixel. One factor covers both axes, because the
 *  canvas scales uniformly. */
const unitsPerPxOf = (svg: SVGSVGElement | null, v: View): number => {
    const ctm = svg && typeof svg.getScreenCTM === "function" ? svg.getScreenCTM() : null;
    if (ctm && ctm.a) return 1 / ctm.a;
    const r = svg?.getBoundingClientRect();
    return r?.width ? v.w / r.width : 1;
};

const PlanView = ({
    model, bays, tier, onTier, walk, showWalk, onToggleWalk, showRoutes, onToggleRoutes,
    selected, onSelectAisle, onSelectBay, onMoveCorridor,
    onMoveCrossing, onMoveColumn, onMovePoint,
}: {
    model: LayoutModel;
    bays: PlacedBay[];
    tier: "low" | "high";
    onTier: (t: "low" | "high") => void;
    walk: WalkStep[] | null;
    showWalk: boolean;
    onToggleWalk: () => void;
    showRoutes: boolean;
    onToggleRoutes: () => void;
    selected: { aisle: string | null; bay: string | null };
    onSelectAisle: (aisle: string) => void;
    onSelectBay: (aisle: string, bay: string) => void;
    onMoveCorridor: (id: string, x: number) => void;
    onMoveCrossing: (idx: number, at: number) => void;
    onMoveColumn: (aisle: string, startAt: number) => void;
    onMovePoint: (idx: number, x: number, y: number) => void;
}) => {
    const { t } = useTranslation();
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [view, setView] = useState<View | null>(null);
    const drag = useRef<
        | { kind: "pan"; startX: number; startY: number; view: View; unitsPerPx: number }
        | { kind: "corridor"; id: string }
        | { kind: "crossing"; idx: number }
        | { kind: "point"; idx: number; dx: number; dy: number }
        | { kind: "column"; aisle: string; startOffset: number }
        | null
    >(null);

    const depthOf = useMemo(() => {
        const m = new Map<string, number>();
        for (const a of model.aisles) m.set(a.aisle, a.rack_depth_m);
        return m;
    }, [model.aisles]);

    // Extent of everything drawn, so "fit" frames the whole shed.
    // Extent in DRAWN (Y-flipped) space, so `fit` frames the whole shed.
    const extent = useMemo(() => {
        const placed = bays.filter(b => !b.unplaced);
        if (placed.length === 0) return { x0: -2, y0: -12, x1: 10, y1: 2 };
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (const b of placed) {
            const d = depthOf.get(b.aisle) ?? 0.6;
            const bx0 = b.side === "b" ? b.x_m! : b.x_m! - d;
            const bx1 = b.side === "b" ? b.x_m! + d : b.x_m!;
            x0 = Math.min(x0, bx0); x1 = Math.max(x1, bx1);
            // Drawn Y is the negation of along_m.
            y0 = Math.min(y0, -(b.along_m! + b.width_m! / 2));
            y1 = Math.max(y1, -(b.along_m! - b.width_m! / 2));
        }
        for (const p of model.points) {
            x0 = Math.min(x0, p.x_m - 1); x1 = Math.max(x1, p.x_m + 1);
            y0 = Math.min(y0, -p.y_m - 1); y1 = Math.max(y1, -p.y_m + 1);
        }
        const pad = 2;
        return { x0: x0 - pad, y0: y0 - pad, x1: x1 + pad, y1: y1 + pad };
    }, [bays, depthOf, model.points]);

    const fit = useCallback(() => {
        setView({ x: extent.x0, y: extent.y0, w: extent.x1 - extent.x0, h: extent.y1 - extent.y0 });
    }, [extent]);

    useEffect(() => { if (!view) fit(); }, [view, fit]);

    const v = view ?? { x: extent.x0, y: extent.y0, w: extent.x1 - extent.x0, h: extent.y1 - extent.y0 };

    // Text size in MODEL units that renders at a roughly constant size on
    // screen: the viewBox is metres, so anything fixed grows and shrinks with
    // the zoom instead of staying legible.
    const labelSize = Math.max(v.h, v.w) * 0.022;

    /** The X of an aisle's pick face — the corridor edge it is picked from. */
    const corridorFaceX = (a: LayoutAisle) => {
        const c = model.corridors.find(x => x.id === a.corridor_id);
        if (!c) return null;
        return a.side === "b" ? c.x_m + c.width_m / 2 : c.x_m - c.width_m / 2;
    };

    /** Client pixels -> model metres.
     *
     *  The canvas is drawn Y-flipped (see the <g> transform below) so that
     *  along_m = 0 is at the BOTTOM: the aisles are aligned on their origin
     *  end and any difference in length shows at the top, which is how the
     *  shed actually reads. SVG's own Y grows downward, so a pointer position
     *  has to be un-flipped on the way back into model space or every drag
     *  would move the wrong way. */
    const toModel = (clientX: number, clientY: number) => {
        const p = toViewBox(svgRef.current, clientX, clientY, v);
        return { x: p.x, y: -p.y };
    };

    const onPointerMove = (e: React.PointerEvent) => {
        const d = drag.current;
        if (!d) return;
        const p = toModel(e.clientX, e.clientY);
        if (d.kind === "pan") {
            // Scale captured at drag start: the CTM changes as the view moves,
            // so reading it each frame would feed back into itself. The
            // transform is uniform, so one factor covers both axes.
            setView({
                ...d.view,
                x: d.view.x - (e.clientX - d.startX) * d.unitsPerPx,
                y: d.view.y - (e.clientY - d.startY) * d.unitsPerPx,
            });
        } else if (d.kind === "corridor") {
            onMoveCorridor(d.id, Math.round(p.x * 10) / 10);
        } else if (d.kind === "crossing") {
            // Free position here; the handler snaps it to a shelf edge.
            onMoveCrossing(d.idx, p.y);
        } else if (d.kind === "column") {
            onMoveColumn(d.aisle, Math.round(p.y * 100) / 100);
        } else if (d.kind === "point") {
            // Move by the DELTA from where the marker was grabbed, not to the
            // cursor. Snapping the centre to the pointer meant the marker
            // jumped the moment you touched it — up to its own radius away —
            // so you were dragging something that had already moved and could
            // never line it up on anything. Half-centimetre steps, because a
            // pack desk is a real place with a real position.
            const round = (n: number) => Math.round(n * 200) / 200;
            onMovePoint(d.idx, round(p.x + d.dx), round(p.y + d.dy));
        }
    };

    const endDrag = (e: React.PointerEvent) => {
        drag.current = null;
        (e.target as Element).releasePointerCapture?.(e.pointerId);
    };

    // Zoom is CTRL + wheel, and while CTRL is held the PAGE must not scroll.
    //
    // This has to be a native listener registered with { passive: false }.
    // React routes onWheel through its own delegated listener, which the
    // browser treats as passive on scroll-blocking events, so calling
    // preventDefault() there is ignored with a console warning and the page
    // scrolls anyway. Registering directly on the element is the only way to
    // actually cancel it.
    //
    // A plain wheel is left alone so the settings page still scrolls normally
    // with the pointer over the plan. Trackpad pinch also arrives as a wheel
    // event with ctrlKey set, so it lands here for free.
    const viewRef = useRef(v);
    viewRef.current = v;
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const handler = (e: WheelEvent) => {
            if (!e.ctrlKey) return;          // plain wheel: let the page scroll
            e.preventDefault();               // ctrl+wheel: zoom only
            const cur = viewRef.current;
            // The point under the cursor, in the same units as the view. Both
            // sides of the sum must be viewBox units or the focus drifts.
            const p = toViewBox(svg, e.clientX, e.clientY, cur);
            const k = e.deltaY > 0 ? 1.15 : 1 / 1.15;
            // Anchor the zoom on the cursor: scale the view about `p` so
            // whatever is under the pointer stays exactly under it.
            setView({
                x: p.x - (p.x - cur.x) * k,
                y: p.y - (p.y - cur.y) * k,
                w: cur.w * k,
                h: cur.h * k,
            });
        };
        svg.addEventListener("wheel", handler, { passive: false });
        return () => svg.removeEventListener("wheel", handler);
    }, []);

    /** Start a point drag, remembering where on the marker it was grabbed. */
    const grabPoint = (e: React.PointerEvent, idx: number, x: number, y: number) => {
        const at = toModel(e.clientX, e.clientY);
        grab(e, { kind: "point", idx, dx: x - at.x, dy: y - at.y });
    };

    const grab = (e: React.PointerEvent, d: NonNullable<typeof drag.current>) => {
        e.stopPropagation();
        drag.current = d;
        (e.target as Element).setPointerCapture?.(e.pointerId);
    };

    return (
        <div className="wlayout-canvas-wrap">
            <div className="wlayout-canvas-tools">
                <button type="button" className="btn btn-secondary btn-sm dispatch-modal-btn" onClick={fit}>
                    <Crosshair size={14} strokeWidth={2} /> {t("wlayout_fit")}
                </button>
                {/* The two tiers are different racking, not one seen from two
                    heights: F and G are shelving below and pallet above, and
                    there are no tunnels up high because the racking runs
                    continuous there. */}
                <div className="wlayout-tier-toggle">
                    {(["low", "high"] as const).map(k => (
                        <button
                            key={k}
                            type="button"
                            aria-pressed={tier === k}
                            className={`btn btn-sm dispatch-modal-btn${tier === k ? " btn-primary" : " btn-secondary"}`}
                            onClick={() => onTier(k)}
                        >
                            {t(`wlayout_tier_${k}`)}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    aria-pressed={showRoutes}
                    className={`btn btn-sm dispatch-modal-btn${showRoutes ? " btn-primary" : " btn-secondary"}`}
                    onClick={onToggleRoutes}
                    title={t("wlayout_routes_hint")}
                >
                    <GitFork size={14} strokeWidth={2} /> {t("wlayout_routes")}
                </button>
                <button
                    type="button"
                    aria-pressed={showWalk}
                    className={`btn btn-sm dispatch-modal-btn${showWalk ? " btn-primary" : " btn-secondary"}`}
                    onClick={onToggleWalk}
                    title={t("wlayout_walk_hint")}
                >
                    <Footprints size={14} strokeWidth={2} /> {t("wlayout_walk")}
                </button>
                {/* What is under the cursor's last click, so the drawing can be
                    read against the locations table. */}
                <span className="wlayout-selected">
                    {selected.aisle
                        ? `${selected.aisle}${selected.bay ?? ""}`
                        : t("wlayout_nothing_selected")}
                </span>
                <span className="wlayout-scale">
                    {t("wlayout_scale").replace("{n}", fmt(v.w, 1))}
                </span>
            </div>
            <svg
                ref={svgRef}
                className="wlayout-canvas"
                viewBox={`${v.x} ${v.y} ${v.w} ${v.h}`}
                preserveAspectRatio="xMidYMid meet"
                onPointerDown={(e) => {
                    drag.current = {
                        kind: "pan", startX: e.clientX, startY: e.clientY, view: v,
                        unitsPerPx: unitsPerPxOf(svgRef.current, v),
                    };
                }}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerLeave={endDrag}
            >
              {/* Everything below is drawn Y-flipped, so along_m = 0 lands at
                  the BOTTOM of the plan and the aisles align on their origin
                  end. Aisle A stays leftmost because X is untouched. */}
              <g transform="scale(1,-1)">
                {/* An END cross-aisle is not a straight bar at one position:
                    it runs along the ENDS of the aisles, and neighbouring
                    corridors need not end flush. Where two differ the joining
                    segment is diagonal — which is what the picker walks. Drawn
                    as a polyline over the resolved per-corridor path. */}
                {showRoutes && model.crossings.filter(c => c.kind === "end").map((c, i) => {
                    const path = endPath(model, c.side ?? "origin");
                    if (path.length === 0) return null;
                    const d = smoothPath(path);
                    return (
                        <g key={c.id ?? `end${i}`} className="wlayout-crossing wlayout-crossing--end">
                            <path d={d} strokeWidth={c.width_m} fill="none" />
                            <title>{t("wlayout_end_crossaisle")}</title>
                        </g>
                    );
                })}



                {/* Corridor centre lines — the drag handle for the whole corridor. */}
                {model.corridors.map((c) => {
                    const len = Math.max(
                        ...model.aisles.filter(a => a.corridor_id === c.id).map(a => aisleLength(a)),
                        1,
                    );
                    return (
                        <g key={c.id} className="wlayout-corridor">
                            <line
                                x1={c.x_m} y1={0} x2={c.x_m} y2={len}
                                onPointerDown={e => grab(e, { kind: "corridor", id: c.id })}
                            />
                            <title>{`${c.id} — ${t("wlayout_drag_corridor")}`}</title>
                        </g>
                    );
                })}

                {/* The RACKING, drawn behind the bays.
                    Without it an aisle is only as long as its stock reaches,
                    which is not how long it is. G holds 53 bays in 63 shelf
                    positions, so its last shelf and a half carry nothing and it
                    drew 2.4m shorter than F — same steel, same nine shelves,
                    same 3.375m start, same far end. Drawing the shelves makes
                    the two obviously equal AND shows where the spare capacity
                    is, which the bays alone cannot say. */}
                {model.aisles.map((a) => {
                    const d = depthOf.get(a.aisle) ?? 0.6;
                    const runs = runsForTier(a, tier);
                    // How many of each shelf's positions are actually slotted.
                    // F holds 58 bays in 63 positions and G 53, so hovering a
                    // bare stretch of shelf answers "is this a gap in the
                    // drawing or a gap in the stock?" without counting squares.
                    const filled = new Map<number, number>();
                    for (const b of bays) {
                        if (b.aisle !== a.aisle || b.unplaced) continue;
                        filled.set(b.run_seq!, (filled.get(b.run_seq!) ?? 0) + 1);
                    }
                    let cursor = a.origin_offset_m;
                    return (
                        <g key={`rack-${a.aisle}`} className="wlayout-rack">
                            {runs.map((r: LayoutRun) => {
                                const start = cursor;
                                cursor += r.shelf_width_m + r.upright_width_m;
                                const face = corridorFaceX(a);
                                if (face == null) return null;
                                return (
                                    <rect
                                        key={r.seq}
                                        className={`wlayout-shelf${
                                            r.locations_per_shelf === 0 ? " wlayout-shelf--gap" : ""}`}
                                        x={a.side === "b" ? face : face - d}
                                        y={start}
                                        width={d}
                                        height={Math.max(r.shelf_width_m, 0.02)}
                                    >
                                        <title>{`${a.aisle} ${t("wlayout_shelf_n")
                                            .replace("{n}", String(r.seq))}${
                                            r.locations_per_shelf === 0
                                                ? ` — ${t("wlayout_run_tunnel")}`
                                                : ` — ${filled.get(r.seq) ?? 0}/${
                                                    r.locations_per_shelf}`}`}</title>
                                    </rect>
                                );
                            })}
                        </g>
                    );
                })}

                {/* Bays. */}
                {bays.filter(b => !b.unplaced).map((b) => {
                    const d = depthOf.get(b.aisle) ?? 0.6;
                    const isSel = selected.aisle === b.aisle
                        && (selected.bay === null || selected.bay === b.bay);
                    return (
                        <rect
                            key={`${b.aisle}-${b.bay}`}
                            className={`wlayout-bay wlayout-bay--${b.side}${isSel ? " wlayout-bay--sel" : ""}`}
                            x={b.side === "b" ? b.x_m! : b.x_m! - d}
                            y={b.along_m! - b.width_m! / 2}
                            width={d}
                            height={Math.max(b.width_m! - 0.04, 0.02)}
                            onPointerDown={(e) => {
                                e.stopPropagation();
                                onSelectBay(b.aisle, b.bay);
                            }}
                        >
                            <title>{`${b.aisle}${b.bay} · ${fmt(b.along_m, 1)}m`}</title>
                        </rect>
                    );
                })}

                {/* Tunnels, drawn AFTER the bays so they are on top and can
                    actually be grabbed — underneath, every bay intercepted the
                    pointer and only the slivers between them were draggable.

                    Like the end cross-aisle a tunnel is a PATH: it cuts several
                    corridors at once and the shelf it replaces sits at a
                    different metre position in each, so it goes diagonal where
                    two neighbours' shelves do not line up. */}
                {model.crossings.map((c, i) => {
                    // No tunnels up high — the racking is continuous there.
                    if (c.kind !== "tunnel" || tier === "high" || !showRoutes) return null;
                    const path = tunnelPath(model, c);
                    if (path.length === 0) return null;
                    // A tunnel that cuts only ONE corridor is legitimate, but a
                    // single-point polyline draws nothing at all — which is how
                    // an older per-aisle tunnel could silently vanish from the
                    // plan. Give it width so it is still visible.
                    const span = path.length > 1
                        ? path
                        : [
                            { ...path[0], x_m: path[0].x_m - c.width_m },
                            { ...path[0], x_m: path[0].x_m + c.width_m },
                        ];
                    const d = smoothPath(span);
                    return (
                        <g key={c.id ?? `tun${i}`}
                           className="wlayout-crossing wlayout-crossing--tunnel">
                            <path d={d} strokeWidth={c.width_m} fill="none" />
                            {/* Fat invisible stroke so the tunnel is easy to
                                catch even when it is drawn thin. */}
                            <path
                                className="wlayout-tunnel-grab"
                                d={d} fill="none"
                                onPointerDown={e => grab(e, { kind: "crossing", idx: i })}
                            />
                            <title>{`${t("wlayout_tunnel")} ${c.label ?? ""} — ${t("wlayout_drag_tunnel")}`}</title>
                        </g>
                    );
                })}

                {/* The LIVE walk order — the sequence a picker is actually sent
                    in, drawn through the bays it visits. Levels are collapsed
                    here: the plan is about where the route goes, not how high
                    it reaches. Consecutive steps in the same bay are one point.

                    This is what makes a routing defect visible rather than
                    arithmetic: a long straight line back across the shed is a
                    bay stranded at the wrong end of the order. */}
                {showWalk && walk && (() => {
                    const at = new Map(bays.filter(b => !b.unplaced)
                        .map(b => [`${b.aisle} ${b.bay}`, b]));
                    const pts: string[] = [];
                    let last = "";
                    for (const st of walk) {
                        const key = `${st.aisle} ${st.bay}`;
                        if (key === last) continue;
                        const b = at.get(key);
                        if (!b) continue;
                        last = key;
                        pts.push(`${b.x_m},${b.along_m}`);
                    }
                    if (pts.length < 2) return null;
                    return (
                        <polyline
                            className="wlayout-walk"
                            points={pts.join(" ")}
                            fill="none"
                            strokeWidth={Math.max(0.06, labelSize * 0.05)}
                        />
                    );
                })()}

                {/* Aisle travel direction, plus the drag handle that sets
                    where the column STARTS. Dragging a column along the
                    corridor is how two mismatched sides are brought into
                    line — G and H do not start at the same point, and that
                    offset is the whole reason their bays do not pair. */}
                {model.aisles.map((a) => {
                    const corridor = model.corridors.find(c => c.id === a.corridor_id);
                    if (!corridor) return null;
                    const len = aisleLength(a, tier);
                    const x = a.side === "b"
                        ? corridor.x_m + corridor.width_m / 2 + (a.rack_depth_m / 2)
                        : corridor.x_m - corridor.width_m / 2 - (a.rack_depth_m / 2);
                    // `len` is aisleLength(), which is the END POSITION — it
                    // already includes origin_offset_m. Adding the offset again
                    // ran the arrow past the racking by exactly one offset,
                    // which is why the walk order overshot the shelves at the
                    // top (worst on B-G, inset two shelves; invisible on H-AA,
                    // which are inset none).
                    const start = a.origin_offset_m;
                    const end = len;
                    const y0 = a.travel === "asc" ? start : end;
                    const y1 = a.travel === "asc" ? end : start;
                    return (
                        <g key={a.aisle} className="wlayout-aisle-mark">
                            {/* The walk order, as repeating chevrons along the
                                aisle rather than one arrowhead at the end: the
                                direction has to be readable anywhere you happen
                                to be looking, not only at the tip. Drawn as a
                                polyline of evenly spaced points so `marker-mid`
                                puts an arrow at each one. */}
                            <polyline
                                className="wlayout-travel"
                                points={Array.from({ length: 7 }, (_, i) => {
                                    const f = i / 6;
                                    return `${x},${y0 + (y1 - y0) * f}`;
                                }).join(" ")}
                                markerMid="url(#wlayout-arrow)"
                                markerEnd="url(#wlayout-arrow)"
                            />
                            {/* Invisible fat grab strip over the column. */}
                            <line
                                className="wlayout-column-grab"
                                x1={x} y1={start} x2={x} y2={end}
                                onPointerDown={(e) => {
                                    onSelectAisle(a.aisle);
                                    grab(e, { kind: "column", aisle: a.aisle, startOffset: a.origin_offset_m });
                                }}
                            />
                            <title>{`${a.aisle} — ${t("wlayout_drag_column")}`}</title>
                        </g>
                    );
                })}
              </g>

                {/* Aisle letters, at BOTH ends of the plan so a column can be
                    identified without scrolling to one edge.

                    Drawn outside the Y-flip, or every glyph would be mirrored.
                    The size is derived from the current view height rather than
                    set in CSS: the viewBox unit is METRES, so a fixed font-size
                    is a physical size that balloons as you zoom out. Scaling
                    with the view keeps the letters constant on screen. */}
                {model.aisles.map((a) => {
                    const corridor = model.corridors.find(c => c.id === a.corridor_id);
                    if (!corridor) return null;
                    const x = a.side === "b"
                        ? corridor.x_m + corridor.width_m / 2 + (a.rack_depth_m / 2)
                        : corridor.x_m - corridor.width_m / 2 - (a.rack_depth_m / 2);
                    const pick = (e: React.PointerEvent) => {
                        e.stopPropagation();
                        onSelectAisle(a.aisle);
                    };
                    return (
                        <g key={`lbl-${a.aisle}`}>
                            <text className="wlayout-aisle-label" fontSize={labelSize}
                                  x={x} y={extent.y0 + labelSize * 1.4} textAnchor="middle"
                                  onPointerDown={pick}>
                                {a.aisle}
                            </text>
                            <text className="wlayout-aisle-label" fontSize={labelSize}
                                  x={x} y={extent.y1 - labelSize * 0.5} textAnchor="middle"
                                  onPointerDown={pick}>
                                {a.aisle}
                            </text>
                        </g>
                    );
                })}

                {/* Route endpoints. Without these a route has no start or end and
                    any saving is only measured across the middle of the job. */}
                {model.points.map((p, i) => (
                    <g key={p.id ?? `p${i}`} className="wlayout-point">
                        {/* A separate, invisible grab target sized from the
                            VIEW rather than from the model. The visible marker
                            is 0.8m across, which on a 40m shed zoomed to fit is
                            a handful of pixels — small enough that a drag
                            usually missed it and panned the plan instead. This
                            keeps the same comfortable target at every zoom
                            without inflating the dot itself. */}
                        <circle
                            className="wlayout-point-grab"
                            cx={p.x_m}
                            cy={p.y_m}
                            r={Math.max(0.9, labelSize * 0.9)}
                            onPointerDown={e => grabPoint(e, i, p.x_m, p.y_m)}
                        />
                        <circle cx={p.x_m} cy={p.y_m} r={0.8} />
                        <text x={p.x_m} y={p.y_m - 1.2} textAnchor="middle">
                            {p.label ?? t(`wlayout_point_${p.kind}`)}
                        </text>
                    </g>
                ))}

                <defs>
                    {/* userSpaceOnUse, sized from the view: the default
                        markerUnits is strokeWidth, which made the arrow scale
                        with the 0.1m travel line and disappear. Sizing it from
                        the view instead keeps it legible at any zoom. */}
                    <marker id="wlayout-arrow" viewBox="0 0 10 10" refX="5" refY="5"
                            markerUnits="userSpaceOnUse"
                            markerWidth={labelSize * 0.7} markerHeight={labelSize * 0.7}
                            orient="auto">
                        <path d="M 0 0 L 10 5 L 0 10 z" />
                    </marker>
                </defs>
            </svg>
        </div>
    );
};

// ─── The editor ─────────────────────────────────────────────────────────────

const WarehouseLayoutEditor = ({ warehouses: allWarehouses }: { warehouses: string[] }) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    // Without cross_warehouse only the person's own warehouse is offered.
    const { canCross, own } = useWarehouseScope();
    const warehouses = useMemo(() => (canCross ? allWarehouses : allWarehouses.filter(w => w === own)), [canCross, own, allWarehouses]);
    const [warehouse, setWarehouse] = useState(warehouses[0] ?? "");
    const [model, setModel] = useState<LayoutModel | null>(null);
    const [exists, setExists] = useState(false);
    const [problems, setProblems] = useState<Problem[]>([]);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [tier, setTier] = useState<"low" | "high">("low");
    // The live walk order, fetched only when the overlay is switched on — it
    // is ~6,900 rows and the panel opens far more often than anyone wants it.
    const [walk, setWalk] = useState<WalkStep[] | null>(null);
    const [showWalk, setShowWalk] = useState(false);
    // Tunnels and cross-aisles cross every corridor, so they can bury the
    // racking when what you are checking is the shelves themselves.
    const [showRoutes, setShowRoutes] = useState(true);
    // The 3D view has its own tier control, and unlike the plan it can show
    // BOTH stacked — which is the point of a 3D view.
    const [tier3d, setTier3d] = useState<"low" | "high" | "both">("both");
    // Live positions are OFF until asked for. They poll, and a panel left open
    // on a desk should not sit hitting the database every few seconds for a
    // display nobody is watching.
    // One shelf open at a time. An aisle is up to sixteen shelves and nearly
    // all of them are identical, so listing them all expanded buried the aisle
    // controls under a screen and a half of repeated numbers. Bulk edit is the
    // normal way in; a single shelf opens when it genuinely differs.
    const [openRun, setOpenRun] = useState<number | null>(null);
    const [showPeople, setShowPeople] = useState(false);
    const [people, setPeople] = useState<LivePerson[]>([]);
    const [selected, setSelected] = useState<{ aisle: string | null; bay: string | null }>(
        { aisle: null, bay: null });
    const [calibration, setCalibration] = useState<Calibration | null>(null);
    const [view3d, setView3d] = useState(false);
    const aliveRef = useRef(true);

    const load = useCallback(async () => {
        if (!warehouse) return;
        setLoaded(false);
        setCalibration(null);
        try {
            const res = await apiFetch(
                `/warehouse-layout?warehouse=${encodeURIComponent(warehouse)}`, {});
            if (!res.ok || !aliveRef.current) return;
            const body = await res.json();
            setExists(!!body.exists);
            setModel(body.model ?? null);
            setProblems(body.problems ?? []);
            setDirty(false);
            setSelected({ aisle: null, bay: null });
        } catch {
            toast.error(t("server_error"), { toastId: "wlayout-load" });
        } finally {
            if (aliveRef.current) setLoaded(true);
        }
    }, [warehouse, t]);

    useEffect(() => {
        aliveRef.current = true;
        void load();
        return () => { aliveRef.current = false; };
    }, [load]);

    // Recomputed per tier: the high racking is a different footprint where an
    // aisle changes above its low shelves (F and G).
    const bays = useMemo(() => (model ? computeBays(model, tier) : []), [model, tier]);

    // Undo history. Every edit goes through patchModel, so recording the model
    // as it was here covers every control on the panel without each one having
    // to remember to.
    //
    // A drag fires patchModel on every pointer move, which would otherwise bury
    // the previous state under a hundred near-identical frames. `coalesce` is a
    // key for "this is a continuation of the same gesture": consecutive patches
    // sharing one are collapsed into a single undo step, so one drag undoes as
    // one action.
    const MAX_UNDO = 60;
    const [history, setHistory] = useState<LayoutModel[]>([]);
    const lastCoalesce = useRef<string | null>(null);

    const patchModel = (fn: (m: LayoutModel) => LayoutModel, coalesce?: string) => {
        setModel((m) => {
            if (!m) return m;
            const sameGesture = coalesce !== undefined && coalesce === lastCoalesce.current;
            if (!sameGesture) {
                setHistory(h => [...h.slice(-(MAX_UNDO - 1)), m]);
            }
            lastCoalesce.current = coalesce ?? null;
            return fn(structuredClone(m));
        });
        setDirty(true);
    };

    /** Step back one edit. Does not touch the server — Save is still what
     *  commits, so undoing past the last save and saving is a valid revert. */
    const undo = () => {
        setHistory((h) => {
            if (h.length === 0) return h;
            setModel(h[h.length - 1]);
            setDirty(true);
            lastCoalesce.current = null;
            return h.slice(0, -1);
        });
    };

    /**
     * Derive from `locations`.
     *
     * Two different actions wear this one function. By default the derivation
     * is FOLDED INTO the saved layout: aisles and bays come from the stock and
     * the drawing — shelf runs, widths, heights, offsets, tunnels, endpoints —
     * is kept. `rebuild` asks for a model derived from scratch, which is how
     * you start again deliberately rather than by pressing the only button
     * there was.
     */
    const seed = async (apply: boolean, rebuild = false) => {
        setBusy("seed");
        try {
            const res = await apiFetch("/warehouse-layout/seed", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, apply, rebuild }),
            });
            const body = await res.json().catch(() => null);
            if (!res.ok) {
                toast.error(body?.message ?? t("server_error"), { toastId: "wlayout-seed" });
                return;
            }
            setModel(body.model);
            setExists(apply);
            setDirty(!apply);
            if (body.notes?.length) {
                toast.info(
                    body.notes.map((n: { detail: string }) => n.detail).join("\n"),
                    { toastId: "wlayout-notes", autoClose: 10000 },
                );
            }
            if (apply) void load();
        } catch {
            toast.error(t("server_error"), { toastId: "wlayout-seed" });
        } finally {
            setBusy(null);
        }
    };

    const save = async () => {
        if (!model) return;
        setBusy("save");
        try {
            const res = await apiFetch("/warehouse-layout", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, model }),
            });
            const body = await res.json().catch(() => null);
            if (!res.ok) {
                toast.error(body?.message ?? t("server_error"), { toastId: "wlayout-save" });
                return;
            }
            setModel(body.model);
            setProblems(body.problems ?? []);
            setExists(true);
            setDirty(false);
            toast.success(
                t("wlayout_saved").replace("{n}", String(body.placed ?? 0)),
                { toastId: "wlayout-save" },
            );
        } catch {
            toast.error(t("server_error"), { toastId: "wlayout-save" });
        } finally {
            setBusy(null);
        }
    };

    /** Fetch the live walk order once, the first time the overlay is wanted. */
    const loadWalk = useCallback(async () => {
        try {
            const res = await apiFetch(
                `/warehouse-layout/walk?warehouse=${encodeURIComponent(warehouse)}`, {});
            if (!res.ok) throw new Error("walk");
            const body = await res.json();
            setWalk(body.steps ?? []);
        } catch {
            toast.error(t("server_error"), { toastId: "wlayout-walk" });
        }
    }, [warehouse, t]);

    // Where people are, refreshed while the 3D view is open and the layer is on.
    //
    // The interval is well inside the server's 90-second window on purpose: a
    // position is only worth showing while it is fresh, and the fade needs a
    // recent age to fade FROM. Polling stops the moment either the view or the
    // layer is switched off, and an in-flight response is dropped rather than
    // being written into a component that has moved on.
    useEffect(() => {
        if (!view3d || !showPeople) { setPeople([]); return; }
        let live = true;
        const pull = async () => {
            try {
                const res = await apiFetch(
                    `/warehouse-layout/live?warehouse=${encodeURIComponent(warehouse)}`, {});
                if (!res.ok) return;
                const body = await res.json();
                if (live) setPeople(body.positions ?? []);
            } catch {
                // A failed poll is not worth a toast every 10 seconds; the
                // figures simply stop updating and then fade out on their own.
            }
        };
        pull();
        const id = window.setInterval(pull, 10_000);
        return () => { live = false; window.clearInterval(id); };
    }, [view3d, showPeople, warehouse]);

    const runCalibration = async () => {
        setBusy("calibration");
        try {
            const res = await apiFetch(
                `/warehouse-layout/calibration?warehouse=${encodeURIComponent(warehouse)}`, {});
            const body = await res.json().catch(() => null);
            if (!res.ok) {
                toast.error(body?.message ?? t("server_error"), { toastId: "wlayout-cal" });
                return;
            }
            setCalibration(body);
        } catch {
            toast.error(t("server_error"), { toastId: "wlayout-cal" });
        } finally {
            setBusy(null);
        }
    };

    const selectedAisle = model?.aisles.find(a => a.aisle === selected.aisle) ?? null;
    // A shelf number means nothing across aisles, so an open shelf must not
    // survive a change of aisle.
    useEffect(() => { setOpenRun(null); }, [selected.aisle]);

    /** The value each measurement shares across an aisle, or null where the
     *  shelves disagree.
     *
     *  A bulk field showing one shelf's number when the others differ would
     *  invite an edit that silently flattens the difference — which for F and
     *  G, pallet beams at one end and parcel shelving at the other, is exactly
     *  the thing not to do. Those fields go blank and read "mixed" instead, and
     *  typing into one is then a deliberate act of levelling the aisle.
     *
     *  Tunnel shelves are excluded from the locations figure only: they hold
     *  nothing by definition, so counting their 0 would make every aisle with
     *  a tunnel look mixed. */
    const commonRun = useMemo(() => {
        const runs = selectedAisle?.runs ?? [];
        const shared = <K extends keyof LayoutRun>(k: K, rows: LayoutRun[]) => {
            if (rows.length === 0) return null;
            const first = rows[0][k];
            return rows.every(r => r[k] === first) ? (first as number) : null;
        };
        const racked = runs.filter(r => r.locations_per_shelf > 0);
        return {
            shelf_width_m: shared("shelf_width_m", runs),
            upright_width_m: shared("upright_width_m", runs),
            shelf_height_m: shared("shelf_height_m", runs),
            shelf_gap_height_m: shared("shelf_gap_height_m", runs),
            locations_per_shelf: shared("locations_per_shelf", racked),
        };
    }, [selectedAisle]);

    const updateAisle = (aisle: string, patch: Partial<LayoutAisle>) =>
        patchModel(m => ({
            ...m,
            aisles: m.aisles.map(a => (a.aisle === aisle ? { ...a, ...patch } : a)),
        }));

    const updateRun = (aisle: string, seq: number, patch: Partial<LayoutRun>) =>
        patchModel(m => ({
            ...m,
            aisles: m.aisles.map(a => (a.aisle !== aisle ? a : {
                ...a,
                runs: a.runs.map(r => (r.seq === seq ? { ...r, ...patch } : r)),
            })),
        }));

    /** Apply one change to EVERY shelf in an aisle.
     *
     *  An aisle's shelves are the same piece of steel repeated, so a
     *  measurement is almost always a fact about all of them: correcting a run
     *  width shelf by shelf is eleven identical edits with eleven chances to
     *  mistype one and end up with an aisle that is subtly the wrong length.
     *
     *  Tunnel shelves are exempt from `locations_per_shelf` and from the shelf
     *  split: a tunnel holds nothing by definition, and bulk-setting 7 across
     *  the aisle would fill the gap in with racking that is not there. Every
     *  other measurement — width, upright, heights — applies to a tunnel too,
     *  because the gap is as wide and as tall as the shelf it replaced. */
    const updateAllRuns = (aisle: string, patch: Partial<LayoutRun>) =>
        patchModel(m => ({
            ...m,
            aisles: m.aisles.map(a => (a.aisle !== aisle ? a : {
                ...a,
                runs: a.runs.map((r) => {
                    if (r.locations_per_shelf > 0) return { ...r, ...patch };
                    const { locations_per_shelf, low_shelves, high_shelves, ...rest } = patch;
                    return { ...r, ...rest };
                }),
            })),
        }));

    /** Set how many SHELVES an aisle has.
     *
     *  A property of the steel, so it is set directly rather than by adding and
     *  removing runs one at a time. Growing copies the last shelf's dimensions;
     *  shrinking drops from the far end. Bays are re-spread evenly afterwards so
     *  bay pitch stays constant down the aisle — a shelf is a fixed width with
     *  dividers at fixed positions, so bays cannot be a different width on one
     *  shelf than the next. */
    const setShelfCount = (aisle: string, count: number) =>
        patchModel((m) => {
            const a = m.aisles.find(x => x.aisle === aisle);
            if (!a || a.runs.length === 0) return m;
            const want = Math.max(1, Math.min(60, Math.trunc(count)));
            const template = a.runs[a.runs.length - 1];
            const runs = [...a.runs];
            while (runs.length < want) runs.push({ ...template, seq: runs.length + 1 });
            runs.length = want;

            // Re-spread over the shelves that actually carry racking; a tunnel
            // shelf keeps none.
            const racked = runs.filter(r => r.locations_per_shelf > 0).length || want;
            const per = Math.ceil(a.bays.length / racked);
            a.runs = runs.map((r, i) => ({
                ...r,
                seq: i + 1,
                locations_per_shelf: r.locations_per_shelf > 0 ? per : 0,
            }));
            return m;
        });

    // Splitting a run is how "4, 6 or 7 bays on a shelf" gets expressed: the
    // run is the unit that carries a bay width, so a differently-spaced stretch
    // of the same aisle has to be its own run.
    const splitRun = (aisle: string, seq: number) =>
        patchModel((m) => {
            const a = m.aisles.find(x => x.aisle === aisle);
            if (!a) return m;
            const idx = a.runs.findIndex(r => r.seq === seq);
            const run = a.runs[idx];
            if (!run || run.locations_per_shelf < 2) return m;
            const first = Math.ceil(run.locations_per_shelf / 2);
            const rest = run.locations_per_shelf - first;
            // Splitting keeps the bay PITCH and divides the shelf width with
            // the bays, so the racking does not silently grow or shrink.
            const pitch = bayWidthOf(run);
            const next: LayoutRun[] = [
                ...a.runs.slice(0, idx),
                { ...run, locations_per_shelf: first, shelf_width_m: pitch * first },
                { ...run, seq: run.seq + 0.5, locations_per_shelf: rest, shelf_width_m: pitch * rest },
                ...a.runs.slice(idx + 1),
            ].map((r, i) => ({ ...r, seq: i + 1 }));
            a.runs = next;
            return m;
        });

    const removeRun = (aisle: string, seq: number) =>
        patchModel((m) => {
            const a = m.aisles.find(x => x.aisle === aisle);
            if (!a || a.runs.length < 2) return m;
            const gone = a.runs.find(r => r.seq === seq);
            a.runs = a.runs.filter(r => r.seq !== seq).map((r, i) => ({ ...r, seq: i + 1 }));
            // Bays do not vanish because a run did — hand them to the last run,
            // so the aisle still covers every bay the locations table has.
            if (gone && a.runs.length > 0) {
                const last = a.runs[a.runs.length - 1];
                last.locations_per_shelf += gone.locations_per_shelf;
                last.shelf_width_m += gone.shelf_width_m;
            }
            return m;
        });

    const addPoint = (kind: string) =>
        patchModel(m => ({
            ...m,
            points: [...m.points, {
                id: `${kind}-${m.points.length + 1}`, kind,
                x_m: 0, y_m: 0, label: null, barcode: null,
            }],
        }));

    const removePoint = (idx: number) =>
        patchModel(m => ({ ...m, points: m.points.filter((_, i) => i !== idx) }));

    /** Set a route endpoint's position. Shared by the plan drag and the typed
     *  fields in the points panel, so the two can never round differently. */
    const movePoint = (idx: number, x: number, y: number) =>
        patchModel(m => ({
            ...m,
            points: m.points.map((p, i) => (i === idx
                ? { ...p, x_m: Number.isFinite(x) ? x : p.x_m, y_m: Number.isFinite(y) ? y : p.y_m }
                : p)),
        // Coalesced per point, so a whole drag or a run of keystrokes is ONE
        // undo step rather than one per pixel.
        }), `point:${idx}`);

    const addTunnel = () =>
        patchModel(m => ({
            ...m,
            crossings: [...m.crossings, {
                id: `tunnel-${m.crossings.length + 1}`, kind: "tunnel",
                at_m: 10, width_m: 1.2, label: null,
                connects: m.corridors.slice(0, 2).map(c => c.id),
            }],
        }));

    const removeCrossing = (idx: number) =>
        patchModel(m => ({ ...m, crossings: m.crossings.filter((_, i) => i !== idx) }));

    const placed = bays.filter(b => !b.unplaced).length;

    return (
        <div className="wlayout">
            <p className="admin-settings-section-hint">{t("wlayout_intro")}</p>

            <div className="wlayout-toolbar">
                <label className="wlayout-field">
                    <span>{t("warehouse")}</span>
                    <select value={warehouse} onChange={e => setWarehouse(e.target.value)}>
                        {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                </label>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm dispatch-modal-btn"
                    disabled={busy !== null || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : (exists ? t("wlayout_refresh_hint") : undefined)}
                    onClick={() => void seed(exists ? false : true)}
                >
                    <RefreshCw size={14} strokeWidth={2} />
                    {exists ? t("wlayout_refresh") : t("wlayout_seed")}
                </button>
                {/* Deriving from scratch DISCARDS the drawing, so it is its own
                    action behind a confirm rather than the same button in a
                    different mood. Refresh above is the everyday one. */}
                {exists && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm dispatch-modal-btn"
                        disabled={busy !== null || readOnly}
                        aria-disabled={readOnly}
                        title={readOnly ? t("read_only_mode") : t("wlayout_rebuild_hint")}
                        onClick={() => {
                            if (!window.confirm(t("wlayout_rebuild_confirm"))) return;
                            void seed(false, true);
                        }}
                    >
                        {t("wlayout_rebuild")}
                    </button>
                )}
                {/* Undo comes before Save so the escape hatch is reachable
                    without crossing the commit. It is local-only: Save is
                    still what writes, so stepping back past the last save and
                    then saving is a valid way to revert. */}
                <button
                    type="button"
                    className="btn btn-ghost btn-sm dispatch-modal-btn"
                    disabled={busy !== null || history.length === 0}
                    title={t("wlayout_undo_hint")}
                    onClick={undo}
                >
                    <Undo2 size={14} strokeWidth={2} /> {t("wlayout_undo")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary btn-sm dispatch-modal-btn"
                    disabled={busy !== null || !dirty || !model || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                    onClick={() => void save()}
                >
                    <Save size={14} strokeWidth={2} /> {t("save")}
                </button>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm dispatch-modal-btn"
                    disabled={busy !== null || !exists}
                    onClick={() => void runCalibration()}
                >
                    <Ruler size={14} strokeWidth={2} /> {t("wlayout_calibrate")}
                </button>
                {dirty && <span className="wlayout-dirty">{t("wlayout_unsaved")}</span>}
            </div>

            {!loaded && <div className="admin-settings-empty">{t("loading")}</div>}

            {loaded && !model && (
                <div className="admin-settings-empty">{t("wlayout_none")}</div>
            )}

            {model && (
                <>
                    <div className="wlayout-stats">
                        <span>{t("wlayout_stat_corridors").replace("{n}", String(model.corridors.length))}</span>
                        <span>{t("wlayout_stat_aisles").replace("{n}", String(model.aisles.length))}</span>
                        <span>{t("wlayout_stat_bays").replace("{n}", String(placed))}</span>
                    </div>

                    <div className="wlayout-viewswitch">
                        <button
                            type="button"
                            aria-pressed={!view3d}
                            className={`walk-exp-chip${!view3d ? " walk-exp-chip--on" : ""}`}
                            onClick={() => setView3d(false)}
                        >
                            {t("wlayout_view_2d")}
                        </button>
                        <button
                            type="button"
                            aria-pressed={view3d}
                            className={`walk-exp-chip${view3d ? " walk-exp-chip--on" : ""}`}
                            onClick={() => setView3d(true)}
                        >
                            {t("wlayout_view_3d")}
                        </button>
                    </div>

                    {view3d && (
                        <>
                            <div className="wlayout-canvas-tools">
                                <div className="wlayout-tier-toggle">
                                    {(["low", "high", "both"] as const).map(k => (
                                        <button
                                            key={k}
                                            type="button"
                                            aria-pressed={tier3d === k}
                                            className={`btn btn-sm dispatch-modal-btn${tier3d === k ? " btn-primary" : " btn-secondary"}`}
                                            onClick={() => setTier3d(k)}
                                        >
                                            {t(`wlayout_tier_${k}`)}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    aria-pressed={showPeople}
                                    className={`btn btn-sm dispatch-modal-btn${showPeople ? " btn-primary" : " btn-secondary"}`}
                                    onClick={() => setShowPeople(v => !v)}
                                >
                                    {t("wlayout_show_people")}
                                </button>
                                {showPeople && (
                                    <span className="wlayout-scale">
                                        {t("wlayout_people_count")
                                            .replace("{n}", String(people.length))}
                                    </span>
                                )}
                            </div>
                            <WarehouseScene3D
                                model={model}
                                walk={showWalk ? walk : null}
                                tier={tier3d}
                                people={showPeople ? people : null}
                            />
                        </>
                    )}

                    <div className="wlayout-split" hidden={view3d}>
                        <PlanView
                            model={model}
                            bays={bays}
                            tier={tier}
                            onTier={setTier}
                            walk={walk}
                            showWalk={showWalk}
                            showRoutes={showRoutes}
                            onToggleRoutes={() => setShowRoutes(v => !v)}
                            onToggleWalk={() => {
                                setShowWalk(v => !v);
                                if (!walk) void loadWalk();
                            }}
                            selected={selected}
                            // Clicking the same aisle again clears it — there
                            // was no other way to get back to nothing selected.
                            onSelectAisle={a => setSelected(cur => (
                                cur.aisle === a && cur.bay === null
                                    ? { aisle: null, bay: null }
                                    : { aisle: a, bay: null }))}
                            onSelectBay={(a, b) => setSelected(cur => (
                                cur.aisle === a && cur.bay === b
                                    ? { aisle: null, bay: null }
                                    : { aisle: a, bay: b }))}
                            onMoveCorridor={(id, x) => patchModel(m => ({
                                ...m,
                                corridors: m.corridors.map(c => (c.id === id ? { ...c, x_m: x } : c)),
                            }), `corridor:${id}`)}
                            onMoveCrossing={(idx, at) => patchModel((m) => {
                                // A tunnel occupies a whole shelf POSITION, so
                                // dragging moves it shelf by shelf rather than
                                // by metres — that is what keeps it stuck to
                                // the racking instead of floating mid-bay.
                                //
                                // A tunnel it does not reach must not be moved
                                // by it, so the shelf index is read off an
                                // aisle the tunnel actually cuts.
                                const c = m.crossings[idx];
                                if (!c) return m;
                                const host = m.aisles.find(a => c.connects.length === 0
                                    || c.connects.includes(a.corridor_id));
                                if (!host) return m;
                                const shelf = shelfIndexAt(host, at);
                                // Store as a count back from the far end when
                                // it lands on the last shelf, so one tunnel can
                                // still span aisles of different shelf counts.
                                const at_shelf = shelf === host.runs.length - 1 ? -1 : shelf;
                                return {
                                    ...m,
                                    crossings: m.crossings.map((x, i) => (i === idx
                                        ? { ...x, at_shelf }
                                        : x)),
                                };
                            }, `crossing:${idx}`)}
                            // Dragging a whole column along the corridor sets
                            // where its racking starts — the control that
                            // brings two mismatched sides into line.
                            onMoveColumn={(aisle, startAt) => patchModel(m => ({
                                ...m,
                                aisles: m.aisles.map(a => (a.aisle === aisle
                                    ? { ...a, origin_offset_m: Math.max(0, startAt) }
                                    : a)),
                            }), `column:${aisle}`)}
                            onMovePoint={movePoint}
                        />

                        <div className="wlayout-side">
                            {/* ── Whole-warehouse constants ───────────────── */}
                            <details className="wlayout-panel wlayout-panel--fold">
                                <summary><Move size={14} strokeWidth={2} /> {t("wlayout_movement")}</summary>
                                <label className="wlayout-field">
                                    <span>{t("wlayout_walk_speed")}</span>
                                    <input
                                        type="number" step="0.05" min="0.1"
                                        value={model.walk_speed_mps}
                                        onChange={e => patchModel(m => ({
                                            ...m, walk_speed_mps: Number(e.target.value),
                                        }))}
                                    />
                                </label>
                                <label className="wlayout-field">
                                    <span>{t("wlayout_cage_mode")}</span>
                                    <select
                                        value={model.cage_mode}
                                        onChange={e => patchModel(m => ({
                                            ...m, cage_mode: e.target.value as LayoutModel["cage_mode"],
                                        }))}
                                    >
                                        {CAGE_MODES.map(c => (
                                            <option key={c} value={c}>{t(`wlayout_cage_${c}`)}</option>
                                        ))}
                                    </select>
                                </label>
                                <p className="wlayout-hint">{t("wlayout_cage_hint")}</p>
                            </details>

                            {/* ── The selected aisle ───────────────────────── */}
                            {selectedAisle ? (
                                <div className="wlayout-panel">
                                    <h5><Layers size={14} strokeWidth={2} />
                                        {t("wlayout_aisle").replace("{a}", selectedAisle.aisle)}</h5>
                                    <div className="wlayout-grid2">
                                        <label className="wlayout-field">
                                            <span>{t("wlayout_racking")}</span>
                                            <select
                                                value={selectedAisle.racking_type}
                                                onChange={e => updateAisle(selectedAisle.aisle, {
                                                    racking_type: e.target.value as "pallet" | "shelving",
                                                })}
                                            >
                                                <option value="shelving">{t("wlayout_shelving")}</option>
                                                <option value="pallet">{t("wlayout_pallet")}</option>
                                            </select>
                                        </label>
                                        <label className="wlayout-field">
                                            <span>{t("wlayout_rack_depth")}</span>
                                            <input type="number" step="0.05" value={selectedAisle.rack_depth_m}
                                                   onChange={e => updateAisle(selectedAisle.aisle, {
                                                       rack_depth_m: Number(e.target.value) })} />
                                        </label>
                                        <label className="wlayout-field">
                                            <span>{t("wlayout_offset")}</span>
                                            <input type="number" step="0.1" value={selectedAisle.origin_offset_m}
                                                   onChange={e => updateAisle(selectedAisle.aisle, {
                                                       origin_offset_m: Number(e.target.value) })} />
                                        </label>
                                        <label className="wlayout-field">
                                            <span>{t("wlayout_bay_one")}</span>
                                            <select
                                                value={selectedAisle.bay_one_at}
                                                onChange={e => updateAisle(selectedAisle.aisle, {
                                                    bay_one_at: e.target.value as "origin" | "far" })}
                                            >
                                                <option value="origin">{t("wlayout_bay_one_origin")}</option>
                                                <option value="far">{t("wlayout_bay_one_far")}</option>
                                            </select>
                                        </label>
                                    </div>
                                    <p className="wlayout-hint">{t("wlayout_offset_hint")}</p>

                                    <h6>{t("wlayout_runs")}</h6>
                                    <p className="wlayout-hint">{t("wlayout_runs_hint")}</p>
                                    <div className="wlayout-grid3">
                                        <label className="wlayout-field">
                                            <span>{t("wlayout_shelf_count")}</span>
                                            <input
                                                type="number" min="1" max="60" step="1"
                                                value={selectedAisle.runs.length}
                                                onChange={e => setShelfCount(
                                                    selectedAisle.aisle, Number(e.target.value))}
                                            />
                                        </label>
                                        <div className="wlayout-field wlayout-field--derived">
                                            <span>{t("wlayout_bays_per_shelf")}</span>
                                            <output>
                                                {selectedAisle.runs.find(r => r.locations_per_shelf > 0)
                                                    ?.locations_per_shelf ?? 0}
                                            </output>
                                        </div>
                                    </div>
                                    <div className="wlayout-bulk">
                                        <h6>{t("wlayout_bulk_all_shelves")
                                            .replace("{n}", String(selectedAisle.runs.length))}</h6>
                                        <p className="wlayout-hint">{t("wlayout_bulk_hint")}</p>
                                        <div className="wlayout-grid3">
                                            <label className="wlayout-field">
                                                <span>{t("wlayout_shelf_width")}</span>
                                                <input
                                                    type="number" step="0.05"
                                                    value={commonRun.shelf_width_m ?? ""}
                                                    placeholder={t("wlayout_mixed")}
                                                    onChange={e => updateAllRuns(selectedAisle.aisle,
                                                        { shelf_width_m: Number(e.target.value) })}
                                                />
                                            </label>
                                            <label className="wlayout-field">
                                                <span>{t("wlayout_locations_per_shelf")}</span>
                                                <input
                                                    type="number" min="0" step="1"
                                                    value={commonRun.locations_per_shelf ?? ""}
                                                    placeholder={t("wlayout_mixed")}
                                                    onChange={e => updateAllRuns(selectedAisle.aisle, {
                                                        locations_per_shelf:
                                                            Math.max(0, Number(e.target.value)),
                                                    })}
                                                />
                                            </label>
                                            <label className="wlayout-field">
                                                <span>{t("wlayout_upright")}</span>
                                                <input
                                                    type="number" step="0.01"
                                                    value={commonRun.upright_width_m ?? ""}
                                                    placeholder={t("wlayout_mixed")}
                                                    onChange={e => updateAllRuns(selectedAisle.aisle,
                                                        { upright_width_m: Number(e.target.value) })}
                                                />
                                            </label>
                                            <label className="wlayout-field">
                                                <span>{t("wlayout_shelf_height")}</span>
                                                <input
                                                    type="number" step="0.05"
                                                    value={commonRun.shelf_height_m ?? ""}
                                                    placeholder={t("wlayout_mixed")}
                                                    onChange={e => updateAllRuns(selectedAisle.aisle,
                                                        { shelf_height_m: Number(e.target.value) })}
                                                />
                                            </label>
                                            <label className="wlayout-field">
                                                <span>{t("wlayout_shelf_gap")}</span>
                                                <input
                                                    type="number" step="0.05"
                                                    value={commonRun.shelf_gap_height_m ?? ""}
                                                    placeholder={t("wlayout_mixed")}
                                                    onChange={e => updateAllRuns(selectedAisle.aisle,
                                                        { shelf_gap_height_m: Number(e.target.value) })}
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    {selectedAisle.runs.map((run) => {
                                        const open = openRun === run.seq;
                                        const tunnel = run.locations_per_shelf === 0;
                                        return (
                                            <div
                                                className={`wlayout-run wlayout-run--collapsible${open ? " is-open" : ""}`}
                                                key={run.seq}
                                            >
                                                <button
                                                    type="button"
                                                    className="wlayout-run-head"
                                                    aria-expanded={open}
                                                    onClick={() => setOpenRun(open ? null : run.seq)}
                                                >
                                                    <ChevronRight
                                                        size={14}
                                                        strokeWidth={2}
                                                        className={`wlayout-run-chev${open ? " is-open" : ""}`}
                                                    />
                                                    <strong>{t("wlayout_shelf_n")
                                                        .replace("{n}", String(run.seq))}</strong>
                                                    <span className="wlayout-run-summary">
                                                        {tunnel
                                                            ? t("wlayout_run_tunnel")
                                                            : t("wlayout_run_summary")
                                                                .replace("{w}", run.shelf_width_m.toFixed(2))
                                                                .replace("{n}", String(run.locations_per_shelf))}
                                                    </span>
                                                </button>
                                                {open && (
                                                    <>
                                                        <div className="wlayout-grid3">
                                                            <label className="wlayout-field">
                                                                <span>{t("wlayout_shelf_width")}</span>
                                                                <input type="number" step="0.05" value={run.shelf_width_m}
                                                                       onChange={e => updateRun(selectedAisle.aisle, run.seq,
                                                                           { shelf_width_m: Number(e.target.value) })} />
                                                            </label>
                                                            <label className="wlayout-field">
                                                                <span>{t("wlayout_locations_per_shelf")}</span>
                                                                <input type="number" min="0" step="1" value={run.locations_per_shelf}
                                                                       onChange={e => updateRun(selectedAisle.aisle, run.seq,
                                                                           { locations_per_shelf: Math.max(0, Number(e.target.value)) })} />
                                                            </label>
                                                            <label className="wlayout-field">
                                                                <span>{t("wlayout_upright")}</span>
                                                                <input type="number" step="0.01" value={run.upright_width_m}
                                                                       onChange={e => updateRun(selectedAisle.aisle, run.seq,
                                                                           { upright_width_m: Number(e.target.value) })} />
                                                            </label>
                                                            {/* Read-only: a low shelf is one
                                                                holding low-tier pick faces,
                                                                and `locations` is the record
                                                                of that. Both counts are
                                                                re-derived on every load and
                                                                save, so an editable field
                                                                here could only ever
                                                                disagree with the stock. */}
                                                            <div className="wlayout-field wlayout-field--derived">
                                                                <span>{t("wlayout_shelves_derived")}</span>
                                                                <output>
                                                                    {t("wlayout_shelves_split")
                                                                        .replace("{low}", String(run.low_shelves))
                                                                        .replace("{high}", String(run.high_shelves))}
                                                                </output>
                                                            </div>
                                                            <label className="wlayout-field">
                                                                <span>{t("wlayout_shelf_height")}</span>
                                                                <input type="number" step="0.05" value={run.shelf_height_m}
                                                                       onChange={e => updateRun(selectedAisle.aisle, run.seq,
                                                                           { shelf_height_m: Number(e.target.value) })} />
                                                            </label>
                                                            <label className="wlayout-field">
                                                                <span>{t("wlayout_shelf_gap")}</span>
                                                                <input type="number" step="0.05" value={run.shelf_gap_height_m}
                                                                       onChange={e => updateRun(selectedAisle.aisle, run.seq,
                                                                           { shelf_gap_height_m: Number(e.target.value) })} />
                                                            </label>
                                                        </div>
                                                        <div className="wlayout-run-actions">
                                                            <button type="button" className="btn btn-secondary btn-sm dispatch-modal-btn"
                                                                    onClick={() => splitRun(selectedAisle.aisle, run.seq)}>
                                                                {t("wlayout_split_run")}
                                                            </button>
                                                            {selectedAisle.runs.length > 1 && (
                                                                <button type="button" className="btn btn-ghost btn-sm dispatch-modal-btn"
                                                                        disabled={readOnly}
                                                                        aria-disabled={readOnly}
                                                                        title={readOnly ? t("read_only_mode") : undefined}
                                                                        onClick={() => removeRun(selectedAisle.aisle, run.seq)}>
                                                                    <Trash2 size={14} strokeWidth={2} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                    <p className="wlayout-hint">
                                        {t("wlayout_run_total")
                                            .replace("{covered}", String(selectedAisle.runs.reduce((n, r) => n + r.locations_per_shelf, 0)))
                                            .replace("{total}", String(selectedAisle.bays.length))
                                            .replace("{len}", fmt(aisleLength(selectedAisle), 1))}
                                    </p>
                                </div>
                            ) : (
                                <div className="wlayout-panel">
                                    <p className="wlayout-hint">{t("wlayout_select_hint")}</p>
                                </div>
                            )}

                            {/* ── Crossings ────────────────────────────────── */}
                            <details className="wlayout-panel wlayout-panel--fold">
                                <summary><Box size={14} strokeWidth={2} /> {t("wlayout_crossings")}
                                    <span className="wlayout-fold-meta">{model.crossings.length}</span></summary>
                                <p className="wlayout-hint">{t("wlayout_crossings_hint")}</p>
                                {model.crossings.map((c, i) => (
                                    <div className="wlayout-row" key={c.id ?? `x${i}`}>
                                        <span className="wlayout-row-label">
                                            {c.kind === "tunnel" ? t("wlayout_tunnel") : t("wlayout_end_crossaisle")}
                                            {c.label ? ` · ${c.label}` : ""}
                                        </span>
                                        <input
                                            type="number" step="0.5" value={c.at_m}
                                            onChange={e => patchModel(m => ({
                                                ...m,
                                                crossings: m.crossings.map((x, j) =>
                                                    (j === i ? { ...x, at_m: Number(e.target.value) } : x)),
                                            }))}
                                        />
                                        <button type="button" className="btn btn-ghost btn-sm dispatch-modal-btn"
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : undefined}
                                                onClick={() => removeCrossing(i)}>
                                            <Trash2 size={14} strokeWidth={2} />
                                        </button>
                                    </div>
                                ))}
                                <button type="button" className="btn btn-secondary btn-sm dispatch-modal-btn"
                                        onClick={addTunnel}>
                                    <Plus size={14} strokeWidth={2} /> {t("wlayout_add_tunnel")}
                                </button>
                            </details>

                            {/* ── Route endpoints ──────────────────────────── */}
                            <details className="wlayout-panel wlayout-panel--fold" open={model.points.length > 0}>
                                <summary><Crosshair size={14} strokeWidth={2} /> {t("wlayout_points")}
                                    <span className="wlayout-fold-meta">{model.points.length}</span></summary>
                                <p className="wlayout-hint">{t("wlayout_points_hint")}</p>
                                {model.points.map((p, i) => (
                                    <div className="wlayout-row" key={p.id ?? `p${i}`}>
                                        <span className="wlayout-row-label">
                                            {t(`wlayout_point_${p.kind}`)}
                                            {(() => {
                                                // Straight-line gap to the nearest placed bay —
                                                // the same anchor the york-drop distance adds to
                                                // every measured trip, so a point drifting away
                                                // from the racking is visible here as metres.
                                                let best: { d: number; key: string } | null = null;
                                                for (const b of bays) {
                                                    if (b.unplaced || b.x_m === null || b.y_m === null) continue;
                                                    const d = Math.hypot(b.x_m - p.x_m, b.y_m - p.y_m);
                                                    if (!best || d < best.d) best = { d, key: `${b.aisle} ${b.bay}` };
                                                }
                                                return best ? (
                                                    <span className="wlayout-point-distance">
                                                        {t("wlayout_point_distance")
                                                            .replace("{m}", (Math.round(best.d * 10) / 10).toFixed(1))
                                                            .replace("{bay}", best.key)}
                                                    </span>
                                                ) : null;
                                            })()}
                                        </span>
                                        {/* Typed as well as dragged. Dragging is
                                            fine for roughly here; a pack desk
                                            measured off a wall wants the number
                                            itself, and no amount of pointer
                                            precision beats typing 12.4. */}
                                        <input
                                            type="number" step="0.1"
                                            className="wlayout-coord-input"
                                            aria-label={t("wlayout_point_x")}
                                            value={p.x_m}
                                            onChange={e => movePoint(
                                                i, Number(e.target.value), p.y_m)}
                                        />
                                        <input
                                            type="number" step="0.1"
                                            className="wlayout-coord-input"
                                            aria-label={t("wlayout_point_y")}
                                            value={p.y_m}
                                            onChange={e => movePoint(
                                                i, p.x_m, Number(e.target.value))}
                                        />
                                        <button type="button" className="btn btn-ghost btn-sm dispatch-modal-btn"
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : undefined}
                                                onClick={() => removePoint(i)}>
                                            <Trash2 size={14} strokeWidth={2} />
                                        </button>
                                    </div>
                                ))}
                                <div className="wlayout-add-points">
                                    {POINT_KINDS.map(k => (
                                        <button key={k} type="button"
                                                className="btn btn-secondary btn-sm dispatch-modal-btn"
                                                onClick={() => addPoint(k)}>
                                            <Plus size={14} strokeWidth={2} /> {t(`wlayout_point_${k}`)}
                                        </button>
                                    ))}
                                </div>
                            </details>
                        </div>
                    </div>

                    {/* ── Problems ─────────────────────────────────────────── */}
                    {problems.length > 0 && (
                        <details className="wlayout-problems wlayout-panel--fold">
                            <summary><AlertTriangle size={14} strokeWidth={2} />
                                {t("wlayout_problems").replace("{n}", String(problems.length))}</summary>
                            <ul>
                                {problems.slice(0, 30).map((p, i) => (
                                    <li key={i}>
                                        <code>{p.kind}</code> {p.detail}
                                        {p.sample?.length ? <em> — {p.sample.slice(0, 10).join(", ")}</em> : null}
                                    </li>
                                ))}
                            </ul>
                            {problems.length > 30 && (
                                <p className="wlayout-hint">
                                    {t("wlayout_problems_more").replace("{n}", String(problems.length - 30))}
                                </p>
                            )}
                        </details>
                    )}

                    {/* ── Calibration ──────────────────────────────────────── */}
                    {calibration && (
                        <div className="wlayout-calibration">
                            <h5><Ruler size={14} strokeWidth={2} /> {t("wlayout_calibration")}</h5>
                            <p className="wlayout-hint">{t("wlayout_calibration_hint")}</p>
                            {calibration.fit ? (
                                <p className="wlayout-fit">
                                    {t("wlayout_fit_summary")
                                        .replace("{speed}", fmt(calibration.fit.walk_speed_mps, 2))
                                        .replace("{handling}", fmt(calibration.fit.handling_s, 1))
                                        .replace("{step}", fmt(calibration.fit.move_penalty_s, 1))
                                        .replace("{r2}", fmt(calibration.fit.r_squared, 3))
                                        .replace("{n}", String(calibration.fit.moves_used))}
                                </p>
                            ) : (
                                <p className="wlayout-fit">{calibration.reason ?? t("wlayout_no_fit")}</p>
                            )}
                            <p className="wlayout-hint">
                                {t("wlayout_coverage")
                                    .replace("{pct}", fmt(calibration.coverage_pct, 1))
                                    .replace("{unmapped}", String(calibration.moves_unmapped))}
                            </p>
                            <div className="admin-settings-table admin-settings-table--5col">
                                <div className="admin-settings-row admin-settings-row--head">
                                    <div>{t("wlayout_band")}</div>
                                    <div>{t("wlayout_moves")}</div>
                                    <div>{t("wlayout_median_m")}</div>
                                    <div>{t("wlayout_median_s")}</div>
                                    <div>{t("wlayout_residual")}</div>
                                </div>
                                {calibration.bands.map(b => (
                                    <div className="admin-settings-row" key={b.band}>
                                        <div>{t(`wlayout_band_${b.band}`)}</div>
                                        <div>{b.n}</div>
                                        <div>{fmt(b.median_metres, 1)}</div>
                                        <div>{fmt(b.median_seconds, 1)}</div>
                                        <div>{fmt(b.residual_seconds, 1)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default WarehouseLayoutEditor;
